import {
    Component,
    HostListener,
    inject,
    Input,
    OnInit,
    OnDestroy,
    Signal,
} from "@angular/core";
import { NavigationStart, Router } from "@angular/router";

import { Subscription } from "rxjs";

import { WINDOW } from "@hermes/core-app-core";
import { FooterColumnState } from "@hermes/domains-shell-states-shell-state";
import { BreakpointService } from "@hermes/utils-generic-services-user-interface";

import { FooterLinksGroupDeprecatedComponent } from "../footer-links-group-deprecated/footer-links-group-deprecated.component";

@Component({
    selector: "h-footer-columns-deprecated",
    imports: [FooterLinksGroupDeprecatedComponent],
    templateUrl: "./footer-columns-deprecated.component.html",
    styleUrls: ["./footer-columns-deprecated.component.scss"],
})
export class FooterColumnsDeprecatedComponent implements OnInit, OnDestroy {
    private readonly breakpointService = inject(BreakpointService);
    private readonly window = inject<Window>(WINDOW);
    private router = inject(Router);

    @Input({ required: true })
    public columns: FooterColumnState[] = [];

    private viewportWidth!: number;
    private readonly isMobile: Signal<boolean> =
        this.breakpointService.isMobile;

    private subscription = new Subscription();

    /**
     * Sync footer columns with the current viewport on window resize.
     * Why :
     * Avoid inconsistent states when switching between mobile and tablet/desktop.
     * - Mobile: only the last column stays open (non-closable).
     * - Tablet/Desktop: all columns are open.
     *
     * Notes :
     * Some devices (mainly Iphone) could trigger the event without having an effective change of the viewport width.
     * When the viewport width remains the same, avoid unnecessary DOM mutations.
     */
    @HostListener("window:resize")
    public onResize(): void {
        if (this.window.innerWidth !== this.viewportWidth) {
            this.viewportWidth = this.window.innerWidth;

            this.updateColumnsByViewport();
        }
    }

    public ngOnInit(): void {
        this.viewportWidth = this.window.innerWidth;
        this.updateColumnsByViewport();
        this.subscription.add(
            this.router.events.subscribe((event) => {
                if (event instanceof NavigationStart && this.isMobile()) {
                    this.closeAllColumnsExceptLast();
                }
            }),
        );
    }

    private closeAllColumnsExceptLast(): void {
        this.columns.forEach((column, index) => {
            column.opened = index === this.columns.length - 1;
        });
    }

    private updateColumnsByViewport(): void {
        if (!this.columns?.length) {
            return;
        }
        if (this.isMobile()) {
            this.closeAllColumnsExceptLast();
        } else {
            this.columns.forEach((column) => (column.opened = true));
        }
    }

    /**
     * Toggle a single footer column (mobile-only).
     *
     * Mobile behavior :
     * - The last column is pinned open and cannot be closed.
     * - If the tapped column is CLOSED, first close all non-last columns, then open it.
     * - If the tapped column is OPEN and NOT the last column, close it.
     *
     * @param column column to toggle
     */
    public handleToggleColumn(
        column: FooterColumnState,
        isLastColumn: boolean,
    ): void {
        if (this.isMobile() && !isLastColumn) {
            const shouldOpenColumn = !column.opened;

            if (shouldOpenColumn) {
                this.updateColumnsByViewport();
            }
            this.toggleColumn(column);
        }
    }

    private toggleColumn(column: FooterColumnState) {
        column.opened = !column.opened;
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
